import React from 'react';
import { ExternalLink, Database, RefreshCw, Shield } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { useLanguage } from '../../context/LanguageContext';

const sources = [
  {
    name: 'LBMA',
    fullName: 'London Bullion Market Association',
    description: 'Daily gold reference prices used by central banks and financial institutions worldwide.',
    url: 'https://www.lbma.org.uk/prices-and-data/precious-metal-prices',
    dataType: 'Gold Prices',
  },
  {
    name: 'exchangerate.host',
    fullName: 'Exchange Rate API',
    description: 'ECB-based foreign exchange rates for currency conversion.',
    url: 'https://exchangerate.host',
    dataType: 'FX Rates',
  },
];

const SourcesSection = () => {
  const { t } = useLanguage();
  
  const dataHandlingItems = [
    { icon: Database, titleKey: 'publicSourcesOnly', descKey: 'publicSourcesDesc' },
    { icon: RefreshCw, titleKey: 'regularUpdates', descKey: 'regularUpdatesDesc' },
    { icon: Shield, titleKey: 'indicativeOnly', descKey: 'indicativeOnlyDesc' }
  ];
  
  return (
    <section id="sources" className="py-20 md:py-28 bg-[#08080c]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">
            {t('transparency')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('dataSources').split(' ')[0]} <span className="text-gradient-gold">{t('dataSources').split(' ').slice(1).join(' ') || 'Sources'}</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {t('sourcesSubtitle')}
          </p>
        </div>

        {/* Sources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {sources.map((source, index) => (
            <Card key={index} className="card-hover bg-[#12121a] border-[#1e1e2e] hover:border-[#D4AF37]/30 rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{source.name}</h3>
                    <p className="text-sm text-gray-500">{source.fullName}</p>
                  </div>
                  <span className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium rounded-full border border-[#D4AF37]/30">
                    {source.dataType}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mb-4">{source.description}</p>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-[#D4AF37] hover:text-[#F5ECD7] transition-colors"
                >
                  {t('visitSource')}
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Data Handling Info */}
        <Card className="bg-[#12121a] border-[#1e1e2e] rounded-2xl">
          <CardContent className="p-6 md:p-8">
            <h3 className="text-lg font-semibold text-white mb-6">{t('howWeHandleData')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {dataHandlingItems.map((item, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-1">{t(item.titleKey)}</h4>
                    <p className="text-sm text-gray-400">{t(item.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <p className="text-center text-sm text-gray-500 mt-8">
          {t('dataDisclaimer')}
        </p>
      </div>
    </section>
  );
};

export default SourcesSection;
